import { PrimeTradeData } from "@services/models/Prime/GetPrimeTradeListModel";
import { ColDef } from "ag-grid-community";
import {
  AcceptedCellRenderer,
  ActiveCellRenderer,
  AmountCellRenderer,
  AutoCellRenderer,
  CashierCellRenderer,
  CompletedCellRenderer,
  ConfirmedCellRenderer,
  DateCellRenderer,
  DisputedCellRenderer,
  ExpiredCellRenderer,
  KycCellRenderer,
  MerchantCellRenderer,
  ReceivedCellRenderer,
  TradeIdCellRenderer,
  UpiCellRenderer,
} from "./PrimeOrderCellRenderers";

// These columns render from eventDetails, but their fields (received, accepted, …)
// are not on the row. AG Grid skips a cell refresh when the field value is unchanged,
// so socket updates never reach the renderer unless this signature changes.
const eventDetailsValueGetter = (params: { data?: PrimeTradeData }) =>
  (params.data?.eventDetails ?? [])
    .map((event) => `${event.eventId}:${event.timestamp ?? ""}`)
    .join("|");

export const createPrimeOrderColumnDefs = (
  countryFilter?: "IN" | "BR",
  tradeType?: string[],
  onExpire?: (tradeId: number) => void,
  onConfirmExpire?: (tradeId: number, reason: any) => void,
  expiringTradeIds?: Set<number>,
): ColDef[] => {
  const baseColumns: ColDef[] = [
    {
      headerName: "Date",
      field: "createdAt",
      flex: 0.8,
      minWidth: 80,
      maxWidth: 120,
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      filter: "agDateColumnFilter",
      cellRenderer: DateCellRenderer,
    },
    {
      headerName: "Trade ID",
      field: "tradeId",
      flex: 1,
      minWidth: 100,
      headerClass: "ag-header-cell-custom header-left",
      cellClass: "ag-cell-custom",
      filter: "agNumberColumnFilter",
      cellRenderer: TradeIdCellRenderer,
      cellStyle: () => ({
        display: "flex",
        justifyContent: "start",
        alignItems: "center",
      }),
    },
    {
      headerName: "Merchant",
      field: "merchantName",
      flex: countryFilter === "IN" ? 1.2 : 1.8,
      minWidth: countryFilter === "IN" ? 120 : 180,
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      filter: "agTextColumnFilter",
      cellRenderer: MerchantCellRenderer,
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: "1.2",
      },
    },
  ];

  if (countryFilter !== "BR") {
    baseColumns.push({
      headerName: "Cashier",
      field: "cashierUsername",
      flex: 1.2,
      minWidth: 120,
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      filter: "agTextColumnFilter",
      cellRenderer: CashierCellRenderer,
      wrapText: true,
      autoHeight: true,
      cellStyle: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        lineHeight: "1.2",
      },
    });

    baseColumns.push({
      headerName: "UPI ID",
      field: "upiId",
      flex: 1.2,
      minWidth: 120,
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      filter: "agTextColumnFilter",
      cellRenderer: UpiCellRenderer,
    });
  }

  if (countryFilter === "BR") {
    baseColumns.push({
      headerName: "Auto",
      field: "isPixAutoSettle",
      flex: 1.2,
      minWidth: 120,
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      filter: "agTextColumnFilter",
      cellRenderer: AutoCellRenderer,
    });
  }

  const remainingColumns: ColDef[] = [
    {
      headerName: "Amount",
      field: "amount",
      flex: 1.8,
      minWidth: 180,
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      filter: false,
      sortable: false,
      cellRenderer: AmountCellRenderer,
    },
    ...(countryFilter === "BR"
      ? [
          {
            headerName: "KYC",
            field: "kyc",
            valueGetter: eventDetailsValueGetter,
            flex: 0.8,
            minWidth: 70,
            maxWidth: 90,
            headerClass: "ag-header-cell-custom",
            cellClass: "ag-cell-custom",
            filter: false,
            sortable: false,
            cellRenderer: KycCellRenderer,
            cellStyle: () => ({
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }),
          },
        ]
      : []),
    {
      headerName: "Received",
      field: "received",
      valueGetter: eventDetailsValueGetter,
      flex: 0.9,
      minWidth: 90,
      maxWidth: 120,
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      filter: false,
      sortable: false,
      cellRenderer: ReceivedCellRenderer,
      cellStyle: () => ({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }),
    },
    {
      headerName: "Accepted",
      field: "accepted",
      valueGetter: eventDetailsValueGetter,
      flex: 0.9,
      minWidth: 90,
      maxWidth: 120,
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      filter: false,
      sortable: false,
      cellRenderer: AcceptedCellRenderer,
      cellStyle: () => ({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }),
    },
    {
      headerName: "Confirmed",
      field: "confirmed",
      valueGetter: eventDetailsValueGetter,
      flex: 0.9,
      minWidth: 90,
      maxWidth: 120,
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      filter: false,
      sortable: false,
      cellRenderer: ConfirmedCellRenderer,
      cellStyle: () => ({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }),
    },
    {
      headerName: "Completed",
      field: "completed",
      valueGetter: eventDetailsValueGetter,
      flex: 0.9,
      minWidth: 90,
      maxWidth: 120,
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      filter: false,
      sortable: false,
      cellRenderer: CompletedCellRenderer,
      cellStyle: () => ({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }),
    },

    ...(countryFilter !== "BR"
      ? [
          {
            headerName: "Disputed",
            field: "disputed",
            valueGetter: eventDetailsValueGetter,
            flex: 0.9,
            minWidth: 90,
            maxWidth: 120,
            headerClass: "ag-header-cell-custom",
            cellClass: "ag-cell-custom",
            filter: false,
            sortable: false,
            cellRenderer: DisputedCellRenderer,
          },
        ]
      : []),

    {
      headerName: "Expired",
      field: "expired",
      valueGetter: eventDetailsValueGetter,
      flex: 0.8,
      minWidth: 80,
      maxWidth: 110,
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      filter: false,
      sortable: false,
      cellRenderer: ExpiredCellRenderer,
      cellStyle: () => ({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }),
    },

    {
      headerName: "Active",
      field: "active",
      valueGetter: eventDetailsValueGetter,
      flex: 0.8,
      minWidth: 80,
      maxWidth: 110,
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      filter: false,
      sortable: false,
      cellRenderer: ActiveCellRenderer,
      cellStyle: () => ({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }),
    },
  ];

  /** ===================== 🔴 EXPIRE ACTION COLUMN (UI ONLY) ===================== */
  if (countryFilter === "BR" && tradeType?.includes("Sell")) {
    remainingColumns.push({
      headerName: "Expire",
      minWidth: 110,
      maxWidth: 130,
      headerClass: "ag-header-cell-custom",
      cellClass: "ag-cell-custom",
      filter: false,
      sortable: false,

      cellRenderer: (params: any) => {
        const tradeId: number = params.data.tradeId;
        const isLoading = expiringTradeIds?.has(tradeId);

        const expiredEvent = params.data.eventDetails?.find(
          (e: any) => String(e.eventId) === "9",
        );

        const completedEvent = params.data.eventDetails?.find((e: any) =>
          ["4", "6"].includes(String(e.eventId)),
        );

        return (
          <button
            data-ignore-row-click="true"
            disabled={isLoading}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();

              if (expiredEvent) {
                onConfirmExpire?.(tradeId, "EXPIRED");
              } else if (completedEvent) {
                onConfirmExpire?.(tradeId, "COMPLETED");
              } else {
                onExpire?.(tradeId);
              }
            }}
            className={`bg-bg-button-delete flex items-center justify-center gap-1 text-[#ffffff] text-[10px] h-[23px] px-3 font-semibold rounded-md
        ${isLoading ? "opacity-60 cursor-not-allowed" : ""}
      `}
          >
            {isLoading && (
              <span className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent" />
            )}
            {isLoading ? "EXPIRING" : "EXPIRE"}
          </button>
        );
      },
    });
  }

  return [...baseColumns, ...remainingColumns];
};
